import {
    createAsyncThunk,
    createSelector,
    createSlice,
} from '@reduxjs/toolkit';

import {
    IReport,
    IReportEntity,
    ISourceSet,
    TSourceSetEntity,
    TStatus,
} from '../../../state/types';

import { TRootState } from '../../../store';

import { getSourceSetModels } from '../../../pages/discovery/selectors';

import {
    IGetTicketsParams,
    IGetWorkflowsParams,
    IPutTicket,
    ITicket,
    ITicketHistory,
    ITicketWithReports,
    IWorkflow,
} from '../types';
import {
    restGetTicket,
    restGetTicketHistory,
    restGetTickets,
    restGetWorkflow,
    restGetWorkflows,
    restPutTicket,
} from '../services';
import { toDateEntity, toTextEntity } from '../../../state/_utils';
import { getUsersCollection } from '../../../state/app/collections';
import { buildEntities, dynamicColumns, withCellStyle } from './grid';
import TimeFormatter from '@/app/helpers/TimeFormatter';

interface IFilters {
    assignedToMe: boolean;
    statuses: { id: string; label: string }[];
}
interface IState {
    ticketList: {
        data: ITicket[] | null;
        status: TStatus;
    };
    filters: IFilters;
    selectedTicket: {
        data: ITicket | null;
        status: TStatus;
    };
    workflows: {
        data: IWorkflow[] | null;
        status: TStatus;
    };
    selectedTicketWorkflow: {
        data: IWorkflow | null;
        status: TStatus;
    };
    ticketHistory: {
        data: ITicketHistory[] | null;
        status: TStatus;
    };
    selectedWorkflow: string | null;
    selectedTicketId: string | null;
    mode: null | 'preview' | 'edit' | 'create';
}

export const initialState: IState = {
    ticketList: {
        data: null,
        status: 'idle',
    },
    filters: {
        assignedToMe: false,
        statuses: [],
    },
    workflows: {
        data: null,
        status: 'idle',
    },
    selectedTicketWorkflow: {
        data: null,
        status: 'idle',
    },
    selectedWorkflow: '',
    selectedTicket: {
        data: null,
        status: 'idle',
    },
    ticketHistory: {
        data: null,
        status: 'idle',
    },
    selectedTicketId: null,
    mode: null,
};

export const fetchTickets = createAsyncThunk(
    'get:ui/tickets',
    async (params?: IGetTicketsParams) => {
        const response = await restGetTickets(params);

        return response;
    }
);

export const fetchTicket = createAsyncThunk(
    'get:ui/tickets/:id',
    async (id: string) => {
        const response = await restGetTicket(id);

        return response;
    }
);

export const fetchTicketHistory = createAsyncThunk(
    'get:ui/tickets/:id/changes',
    async (id: string) => {
        return await restGetTicketHistory(id);
    }
);

export const fetchWorkflows = createAsyncThunk(
    'get:ui/tickets/workflows',
    async (params?: IGetWorkflowsParams) => {
        const response = await restGetWorkflows(params);

        return response;
    }
);

export const fetchWorkflow = createAsyncThunk(
    'get:ui/tickets/workflows/:id',
    async (id: string) => {
        const response = await restGetWorkflow(id);

        return response;
    }
);

export const updateTicket = createAsyncThunk(
    'put:ui/tickets/:id',
    async (
        { ticket, id }: { ticket: IPutTicket; id: string },
        { rejectWithValue }
    ) => {
        try {
            const response = await restPutTicket(ticket, id);
            return response;
        } catch (e: any) {
            throw rejectWithValue({
                status: e.status,
                errorMsg: e.responseJSON.message,
            });
        }
    }
);

const tickets = createSlice({
    name: 'tickets',
    initialState,
    reducers: {
        selectTicketId(state, action) {
            state.selectedTicketId = action.payload;
            state.mode = 'preview';
        },
        changeTicketPaneMode(state, action) {
            state.mode = action.payload;
        },
        resetTicket(state) {
            state.mode = null;
            state.selectedTicket.data = null;
            state.selectedTicket.status = 'idle';
        },
        setAssignedToMe(state, action) {
            state.filters.assignedToMe = action.payload;
        },
        toggleAssignedToMe(state) {
            state.filters.assignedToMe = !state.filters.assignedToMe;
        },
        setStatusesFilter(state, action) {
            state.filters.statuses = action.payload;
        },
        resetFilters(state) {
            state.filters = initialState.filters;
        },
        selectWorkflow(state, action) {
            state.selectedWorkflow = action.payload;
        },
    },
    extraReducers: (builder) => {
        builder.addCase(fetchTickets.pending, (state) => {
            state.ticketList.status = 'loading';
        });
        builder.addCase(fetchTickets.rejected, (state) => {
            state.ticketList.status = 'error';
        });
        builder.addCase(fetchTickets.fulfilled, (state, action) => {
            state.ticketList.status = 'done';
            state.ticketList.data = action.payload.content;
        });

        builder.addCase(fetchTicketHistory.pending, (state) => {
            state.ticketHistory.status = 'loading';
        });
        builder.addCase(fetchTicketHistory.rejected, (state) => {
            state.ticketHistory.status = 'error';
        });
        builder.addCase(fetchTicketHistory.fulfilled, (state, action) => {
            state.ticketHistory.status = 'done';
            state.ticketHistory.data = action.payload;
        });
        builder.addCase(fetchTicket.pending, (state) => {
            state.selectedTicket.data = null;
            state.selectedTicket.status = 'loading';
        });
        builder.addCase(fetchTicket.rejected, (state) => {
            state.selectedTicket.status = 'error';
        });
        builder.addCase(fetchTicket.fulfilled, (state, action) => {
            state.selectedTicket.status = 'done';
            state.selectedTicket.data = action.payload;
        });
        builder.addCase(fetchWorkflows.pending, (state) => {
            state.workflows.status = 'loading';
        });
        builder.addCase(fetchWorkflows.rejected, (state) => {
            state.workflows.status = 'error';
        });
        builder.addCase(fetchWorkflows.fulfilled, (state, action) => {
            state.workflows.status = 'done';
            state.workflows.data = action.payload;
        });
        builder.addCase(fetchWorkflow.pending, (state) => {
            state.selectedTicketWorkflow.data = null;
            state.selectedTicketWorkflow.status = 'loading';
        });
        builder.addCase(fetchWorkflow.rejected, (state) => {
            state.selectedTicketWorkflow.status = 'error';
        });
        builder.addCase(fetchWorkflow.fulfilled, (state, action) => {
            state.selectedTicketWorkflow.status = 'done';
            state.selectedTicketWorkflow.data = action.payload;
        });
    },
});

const getCurrent = (state: TRootState) => state.ui.tickets as IState;
export const getTicketsMode = (state: TRootState) => state.ui.tickets.mode;

export const getTickets = (state: TRootState) =>
    getCurrent(state).ticketList.data;
export const getTicketsStatus = (state: TRootState) =>
    getCurrent(state).ticketList.status;
export const getSelectedTicketId = (state: TRootState) =>
    getCurrent(state).selectedTicketId;

export const getTicket = (state: TRootState) =>
    getCurrent(state).selectedTicket.data;
export const getSelectedTicketStatus = (state: TRootState) =>
    getCurrent(state).selectedTicket.status;

export const getSelectedWorkflow = (state: TRootState) =>
    getCurrent(state).selectedWorkflow;
export const getWorkflowsStatuses = (state: TRootState) =>
    getCurrent(state).workflows.data?.flatMap((workflow) => workflow.statuses);

export const getWorkflows = (state: TRootState) =>
    getCurrent(state).workflows.data;

export const getSelectedTicketAvailableStatuses = (state: TRootState) =>
    getCurrent(state).selectedTicketWorkflow.data?.statuses;

export const getTicketsSourceSetModel = createSelector(
    [getSourceSetModels],
    (models) => models.tickets || null
);

export const getTicketsPaneVisible = createSelector(
    [getTicketsMode],
    (mode) => !!mode
);

export const getFilters = (state: TRootState) => getCurrent(state).filters;

export const getAssignedToMeFilter = (state: TRootState) =>
    getCurrent(state).filters.assignedToMe;

export const getStatusesFilter = (state: TRootState) =>
    getCurrent(state).filters.statuses;

export const getTicketAsReport = createSelector(
    [getTicket, getUsersCollection, getWorkflowsStatuses],
    (ticket, users, statuses): ITicketWithReports | null => {
        if (!ticket) {
            return null;
        }
        const reports: IReport[] = [
            {
                id: 'details' + ticket.id,
                label: 'Basic data',
                //@ts-ignore
                entities: [
                    toTextEntity('name', 'Name', ticket.name),
                    toTextEntity(
                        'status',
                        'Status',
                        statuses?.find(
                            (status) => status.name === ticket.status
                        )?.label
                    ),
                    toTextEntity(
                        'assignedUser',
                        'Assigned user',
                        ticket.assignedUser
                            ? users?.entities[ticket.assignedUser]?.login
                            : undefined
                    ),

                    toDateEntity('createdAt', 'Created at', ticket.createdAt),
                    toDateEntity('updatedAt', 'Updated at', ticket.updatedAt),
                    toTextEntity(
                        'description',
                        'Description',
                        ticket.description
                    ),

                    toTextEntity('notice', 'Notice', ticket.notice?.content),
                ].filter(Boolean),
                _meta: {
                    expanded: true,
                },
            },
        ];
        if (ticket.domainData && Object.keys(ticket.domainData).length > 0) {
            reports.push({
                id: 'domainData' + ticket.id,
                label: 'Domain Data',
                entities: Object.entries(ticket.domainData)
                    .map(([key, value]) => {
                        if (TimeFormatter.isValidDate(value)) {
                            return toDateEntity(
                                key,
                                `domainData.${key}`,
                                //@ts-ignore
                                value
                            );
                        } else {
                            return toTextEntity(
                                key,
                                `domainData.${key}`,
                                //@ts-ignore
                                value
                            );
                        }
                    })
                    .filter(Boolean) as IReportEntity[],
                _meta: {
                    expanded: true,
                },
            });
        }
        return {
            id: 'ticket' + ticket.id,
            reports,
            _meta: {
                menu: [],
            },
        };
    }
);

export const getTicketHistoryStatus = (state: TRootState) =>
    getCurrent(state).ticketHistory.status;
export const getTicketHistory = (state: TRootState) =>
    getCurrent(state).ticketHistory.data;

export const getTicketHistoryAsSourceSet = createSelector(
    [getTicketHistory],
    (ticketHistory): ISourceSet | null =>
        ticketHistory && {
            id: 'ticketHistory',
            definitionId: 'ticketHistory',
            label: 'Ticket History',
            attributes: [
                {
                    id: 'general',
                    label: 'General',
                    type: 'parent',
                    collapsed: false,
                    children: [
                        {
                            id: 'author',
                            label: 'Reported by',
                            type: 'text',
                            showAlways: true,
                        },
                        {
                            id: 'createdAt',
                            label: 'Created at',
                            type: 'date',
                            showAlways: true,
                        },
                        {
                            id: 'content',
                            label: 'Notice',
                            type: 'text',
                            showAlways: true,
                        },
                    ],
                },
                {
                    id: 'changes',
                    label: 'Changes',
                    type: 'parent',
                    collapsed: false,
                    children: [
                        withCellStyle({
                            id: 'status',
                            label: 'Status',
                            type: 'text',
                            showAlways: true,
                        }),
                        withCellStyle({
                            id: 'assignedUser',
                            label: 'Assigned user',
                            type: 'text',
                            showAlways: true,
                        }),
                        withCellStyle({
                            id: 'name',
                            label: 'Name',
                            type: 'text',
                            showAlways: false,
                        }),
                        withCellStyle({
                            id: 'description',
                            label: 'Description',
                            type: 'text',
                            showAlways: false,
                        }),
                        ...dynamicColumns(ticketHistory).map(withCellStyle),
                    ],
                },
            ],
            layersAttributes: [],
            _meta: {},
            entities: buildEntities(ticketHistory).map(
                (row): TSourceSetEntity => ({
                    ...row,
                    id: row.id.toString(),
                    _meta: {},
                })
            ),
        }
);

export const getTicketHistorySourceSetModel = createSelector(
    [getSourceSetModels],
    (models) => models.ticketHistory || null
);

export const {
    selectTicketId,
    changeTicketPaneMode,
    resetTicket,
    selectWorkflow,
    setAssignedToMe,
    toggleAssignedToMe,
    setStatusesFilter,
    resetFilters,
} = tickets.actions;

export default tickets.reducer;
